import React from 'react';
import { Layers, Maximize2, Pause, Pencil, Play, RotateCcw, SkipBack, SkipForward } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import type { VideoManifest, VideoPlaybackState } from '@/types/videoManifest';
import { SegmentedTimeline } from './SegmentedTimeline';
import { formatTime } from '@/lib/studio/format';
import { ComingSoon, StudioPlayerControlsB } from './StudioPlayerControlsB';

export interface StudioPlayerControlsProps {
  isVideoMode: boolean;
  slideCount: number;
  currentIndex: number;
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  progress: number;
  segmented: { manifest: VideoManifest; playbackState: VideoPlaybackState } | null;
  onTimelineSeek: (time: number) => void;
  onTimelineSlideClick: (slideId: string) => void;
  onSeek: (time: number) => void;
  onRestart: () => void;
  onPrev: () => void;
  onNext: () => void;
  onPlayPause: () => void;
  isEditMode: boolean;
  onEnterEdit: () => void;
  onFullscreen: () => void;
  showFilmstrip: boolean;
  onToggleFilmstrip: () => void;
  /** Direction B: the row sits over the video in full screen, with only playback and the way out. */
  fullscreen?: boolean;
  /** Direction B on a phone: only what fits in the row. */
  phone?: boolean;
  /** Direction B: the scene on stage, shown in the row while the timeline below does the scrubbing. */
  scene?: { title: string; number: number; total: number };
  /** Direction B: false while the stage shows a scene that is not built yet (its plan or a draft). */
  playable?: boolean;
  /** `b`: Direction B's one 44 px row (beta only). */
  variant?: 'b';
}

export const StudioPlayerControls: React.FC<StudioPlayerControlsProps> = (props) =>
  props.variant === 'b' ? <StudioPlayerControlsB {...props} /> : <TodaysControls {...props} />;

/** Restart, previous, play, next; the time and the timeline; Edit, fullscreen and the filmstrip switch. */
const TodaysControls: React.FC<StudioPlayerControlsProps> = ({
  isVideoMode,
  slideCount,
  currentIndex,
  isPlaying,
  currentTime,
  duration,
  progress,
  segmented,
  onTimelineSeek,
  onTimelineSlideClick,
  onSeek,
  onRestart,
  onPrev,
  onNext,
  onPlayPause,
  isEditMode,
  onEnterEdit,
  onFullscreen,
  showFilmstrip,
  onToggleFilmstrip,
}) => (
  <div className="h-12 flex items-center gap-3 px-4">
    <TooltipProvider delayDuration={300}>
      {/* Left: Player Controls */}
      <div className="flex items-center gap-1 flex-shrink-0">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon" onClick={onRestart} className="w-8 h-8 studio-text-secondary hover:studio-text-primary">
              <RotateCcw className="w-3.5 h-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Restart</TooltipContent>
        </Tooltip>

        {/* Previous slide - only in video mode with multiple slides */}
        {isVideoMode && slideCount > 1 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={onPrev}
                disabled={currentIndex <= 0}
                className="w-8 h-8 studio-text-secondary hover:studio-text-primary disabled:opacity-30"
              >
                <SkipBack className="w-3.5 h-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Previous</TooltipContent>
          </Tooltip>
        )}

        <button
          onClick={onPlayPause}
          className="w-10 h-10 rounded-full flex items-center justify-center studio-ai-button shadow-lg active:scale-95 transition-all"
        >
          {isPlaying ? (
            <Pause className="w-4 h-4 fill-current" />
          ) : (
            <Play className="w-4 h-4 fill-current ml-0.5" />
          )}
        </button>

        {/* Next slide - only in video mode with multiple slides */}
        {isVideoMode && slideCount > 1 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={onNext}
                disabled={currentIndex >= slideCount - 1}
                className="w-8 h-8 studio-text-secondary hover:studio-text-primary disabled:opacity-30"
              >
                <SkipForward className="w-3.5 h-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Next</TooltipContent>
          </Tooltip>
        )}
      </div>

      {/* Center: Time + Timeline */}
      <div className="flex-1 flex items-center gap-2">
        <span className="text-[11px] font-mono studio-text-secondary w-10 text-right flex-shrink-0">
          {formatTime(currentTime)}
        </span>

        {/* Timeline - Use SegmentedTimeline in composition mode, simple bar otherwise */}
        {segmented ? (
          <div className="flex-1">
            <SegmentedTimeline
              manifest={segmented.manifest}
              playbackState={segmented.playbackState}
              onSeek={onTimelineSeek}
              onSlideClick={onTimelineSlideClick}
              interactive={true}
              showTransitions={false}
            />
          </div>
        ) : (
          <div
            className="flex-1 h-6 flex items-center group cursor-pointer"
            onClick={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              const x = e.clientX - rect.left;
              const percentage = x / rect.width;
              onSeek(percentage * duration);
            }}
            onMouseDown={(e) => {
              const timeline = e.currentTarget;
              const handleDrag = (moveEvent: MouseEvent) => {
                const rect = timeline.getBoundingClientRect();
                const x = Math.max(0, Math.min(moveEvent.clientX - rect.left, rect.width));
                const percentage = x / rect.width;
                onSeek(percentage * duration);
              };
              const handleUp = () => {
                document.removeEventListener('mousemove', handleDrag);
                document.removeEventListener('mouseup', handleUp);
              };
              document.addEventListener('mousemove', handleDrag);
              document.addEventListener('mouseup', handleUp);
            }}
          >
            <div className="w-full h-1.5 bg-gray-200 dark:bg-zinc-800 rounded-full overflow-hidden relative group-hover:h-2 transition-all">
              <div
                className="h-full bg-primary rounded-full transition-[width]"
                style={{ width: `${progress}%` }}
              />
              <div
                className={cn(
                  "absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-white shadow-md border-2 border-primary",
                  "opacity-0 group-hover:opacity-100 transition-opacity",
                  "pointer-events-none"
                )}
                style={{ left: `calc(${progress}% - 6px)` }}
              />
            </div>
          </div>
        )}

        <span className="text-[11px] font-mono studio-text-secondary w-10 flex-shrink-0">
          {formatTime(duration)}
        </span>
      </div>

      {/* Right: Edit + Controls */}
      <div className="flex items-center gap-1 flex-shrink-0">
        {/* Edit button - Shows when not in edit mode */}
        {!isEditMode && (
          <ComingSoon>
            <Button
              variant="ghost"
              size="sm"
              disabled
              onClick={onEnterEdit}
              className="h-8 px-3 gap-1.5 text-xs font-medium studio-text-secondary"
            >
              <Pencil className="w-3.5 h-3.5" />
              <span>Edit</span>
            </Button>
          </ComingSoon>
        )}

        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon" onClick={onFullscreen} className="w-8 h-8 studio-text-secondary hover:studio-text-primary">
              <Maximize2 className="w-3.5 h-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Fullscreen</TooltipContent>
        </Tooltip>

        {/* Filmstrip toggle - only in video mode */}
        {isVideoMode && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={onToggleFilmstrip}
                className={cn(
                  "w-8 h-8 studio-text-secondary hover:studio-text-primary",
                  showFilmstrip && "bg-black/5 dark:bg-white/5"
                )}
              >
                <Layers className="w-3.5 h-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{showFilmstrip ? 'Hide' : 'Show'} timeline</TooltipContent>
          </Tooltip>
        )}
      </div>
    </TooltipProvider>
  </div>
);
